import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { of } from 'rxjs/observable/of';
import { SUPER_ADMIN, USER_INFO } from '../util/constants';
import { DateUtil } from '../util/dateUtil';
import { NGaugeService } from './shared/ngauge.service';
import { UserService } from './user.service';
import { AuthService } from './shared/auth.service';

@Injectable()
export class AccountsService {

  constructor(
    private userService: UserService,
    private httpClient: HttpClient,
    private authService: AuthService,
    private ngaugeService: NGaugeService) {
  }

  getAllContacts() {
    const columnFilter = '"fields": ["id", "firstName", "lastName", "accountId", "phoneNumber", "email", "SID"]';
    const includeFilter = '"include": [{"relation": "account"}]';
    const filter = '{' + columnFilter + ',' + includeFilter + '}';
    const params = { params: this.ngaugeService.getHttpParams(filter) };
    return this.httpClient.get(this.ngaugeService.getMeldURL('CONTACT'), params);
  }

  getAllZipCodes() {
    const columnFilter = '"fields": ["id", "zipCode","SID"]';
    const filter = '{' + columnFilter + '}';
    const params = { params: this.ngaugeService.getHttpParams(filter) };
    return this.httpClient.get(this.ngaugeService.getMeldURL('ACCOUNT_OPTIMIZED'), params);
  }

  getRateById(rateId) {
    const whereCondition = '"where": {"id": ' + rateId + '}';
    const filter = '{' + whereCondition + '}';
    const params = { params: this.ngaugeService.getHttpParams(filter) };
    return this.httpClient.get(this.ngaugeService.getMeldURL('RATE'), params);
  }

  getTrackById(trackId) {
    const whereCondition = '"where": {"id": ' + trackId + '}';
    const filter = '{' + whereCondition + '}';
    const params = { params: this.ngaugeService.getHttpParams(filter) };
    return this.httpClient.get(this.ngaugeService.getMeldURL('TRACK_ACCOUNT'), params);
  }

  getAccountNoteById(accountNoteId) {
    return this.httpClient.get(this.ngaugeService.getMeldURL('ACCOUNT_NOTE') + '/' + accountNoteId);
  }

  deleteAccountNote(accountNoteId) {
    return this.httpClient.delete(this.ngaugeService.getMeldURL('REMOVE_ACCOUNT_NOTE') + '/' + accountNoteId);
  }

  updateRateDetails(rate) {
    return this.httpClient.put(this.ngaugeService.getMeldURL('UPDATERATE'), { 'ratedetail': rate, 'SID': this.authService.getSID() });
  }

  createTracking(trackData) {
    return this.httpClient.post(this.ngaugeService.getMeldURL('TRACKING'), { 'trackingdetail': trackData, 'SID': this.authService.getSID() });
  }

  updateTracking(trackDetails) {
    return this.httpClient.put(this.ngaugeService.getMeldURL('UPDATETRACKING'), { 'trackingdetail': trackDetails , 'SID': this.authService.getSID()});
  }

  createAccountNote(accountNoteData) {
    accountNoteData['SID']= this.authService.getSID();
    accountNoteData['SourceID']= this.authService.getSID();
    return this.httpClient.post(this.ngaugeService.getMeldURL('CREATE_ACCOUNT_NOTE'), accountNoteData);
  }

  updateAccountNote(accountNoteDetails) {
    return this.httpClient.put(this.ngaugeService.getMeldURL('UPDATE_ACCOUNT_NOTE')  + '/' + accountNoteDetails['id'], { 'note': accountNoteDetails['notes'] , 'SID': this.authService.getSID()}, );
  }

  addRateDetails(rates) {
    return this.httpClient.post(this.ngaugeService.getMeldURL('CREATERATE'), { 'ratedetail': rates, 'SID': this.authService.getSID() });
  }

  getAccountDetails() {
    const isClusterproperty = this.ngaugeService.getIsClusterProperty();
    const userProperty = this.userService.getLoggedInUserProperty();
    const columnFilter = '"fields":["account", "address", "salesPerson", "id", "zipCode", "tags", "city", "state",' +
      '"property", "type", "marketSegment", "status", "SID"]';
    const includeContact = '{"relation": "contact","scope":{' +
      '"fields": ["firstName", "email", "id","phoneNumber", "accountId", "workEmail", "lastName", "type", "city", "zip", "fax", "SID"]' +
      // ',"where": {"type":"primary"}' +
      '}}';
    const includeGroup = '{"relation":"groupinfo", "scope": {"fields":["groupName","id"]}}';
    const includeTable = '"include": [' + includeContact + ',' + includeGroup + ']';
    const whereCondition = '"where":{"SID":"' + this.authService.getSID() + '"}';
    // const filter = isClusterproperty ? '{' + whereCondition + ',' + columnFilter + ',' + includeTable + '}' : '{' + columnFilter + ',' + includeTable + '}';
    const filter = '{' + whereCondition + ',' + columnFilter + ',' + includeTable + '}';
    const params = { params: this.ngaugeService.getHttpParams(filter) };
    return this.httpClient.get(this.ngaugeService.getMeldURL('ACCOUNT_OPTIMIZED'), params).map((accounts: any) => {
      accounts.forEach(account => {
        if (account.contact && account.contact.length) {
          account.contact = this.ngaugeService.appendFNameAndLName(account.contact, account.SID);
        }
      });
      return accounts;
    });
  }

  getAccountNameById(accountId) {
    const whereCondition = '"where": {"id":' + accountId + '}';
    const columnFilter = '"fields": ["status","account","id","SID"]';
    const filter = '{' + whereCondition + ',' + columnFilter + '}';
    const params = { params: this.ngaugeService.getHttpParams(filter) };
    return this.httpClient.get(this.ngaugeService.getMeldURL('ACCOUNT_OPTIMIZED'), params);
  }

  getAccountInfo(accountId, isAccountAccessible) {
    const userProperty = this.userService.getLoggedInUserProperty();
    const contactFields = '"fields": ["id", "image", "firstName", "lastName", "designation", "phoneNumber", "fax",' +
      ' "type", "email", "workEmail", "assistant", "address", "city", "state", "zip", "assistantPhoneNo", "SID"]';
    const includeContact = '{"relation": "contact", "scope":{' + contactFields + '}}';
    const salesActivityFields = '"fields": ["id", "createdBy", "startDate", "endDate", "completed",' +
      '"type", "objective", "salesActivityType", "assigned", "completionDate", "result", "SID"]';
    const includeAdminUser = '"include": {"relation": "adminuser", "scope":{"fields":["id","firstName","lastName", "SID"]}}';
    const includeSalesActivity = '{"relation": "salesactivity", "scope":{' +
      salesActivityFields + ',' + includeAdminUser + '}}';
    const businessOpportunityFields = '"fields": ["id", "companyandcontactinfo", "businesspotential", "createdBy", "createdOn", "SID"]';
    const orderBy = '"order":"createdOn desc"';
    const includeBusinessOpportunity = '{"relation":"businessopportunity", "scope": {' + businessOpportunityFields + ',' +
      orderBy + ',' + includeAdminUser + ', "where":{"property": "' + userProperty + '"}}}';
    const rateFields = '"fields": ["id", "isActive", "type", "rate", "startDate", "endDate", "forecast", "SID"]';
    const includeRate = '{"relation": "rate", "scope": {' + rateFields + ', "where":{"property": "' + userProperty + '"}}}';
    const trackingFields =
      '"fields": ["id", "eventname", "date", "type", "property", "night", "rate", "revenue",' +
      '"points", "roomRental", "banquetBar", "banquetFood", "trackingType", "SID"]';
    const includeTrackings =
      '{"relation": "tracking", "scope": {' + trackingFields + ', "where":{"property": "' + userProperty + '"}}}';
    const accountNotesFields = '"fields": ["createdBy", "note", "SourceID", "accountID", "SID", "id", "date"]';
    const includeAccountNotes = '{"relation": "accountNotes", "scope": {' + accountNotesFields + ', "where":{"property": "' + userProperty + '"}}}';
    const groupFields =
      '"fields" : ["id", "arrivalDate","endDate", "groupName", "contact", "status", "property", "roomBlockInfo", ' +
      '"coordinator", "salesPerson", "SID"]';
    const includeGroup =
      '{"relation": "groupinfo", "scope":{' + groupFields + ', "where":{"property": "' + userProperty + '"}}}';
    const functionFields =
      '"fields": ["id", "functionDate", "functionName","contact", "status", "cateringManager", "salesPerson", "contactId"' +
      ', "groupname", "SID"]';
    const includeFunction =
      '{"relation": "accountfunction", "scope":{' + functionFields + ', "where":{"property": "' + userProperty + '"},' +
      '"include":[{"relation": "contact", "scope":{"fields": ["id", "firstName", "lastName", ' +
      ' "phoneNumber", "type", "email", "workEmail"]}},{"relation": "adminuser", "scope":{"fields":["id","firstName","lastName", "SID"]}}]}}';
    let includeTables = '';
    if (isAccountAccessible) {
      includeTables = '"include" : [' + includeContact + ',' + includeSalesActivity + ',' + includeRate + ',' +
        includeTrackings + ',' + includeGroup + ',' + includeFunction + ',' + includeBusinessOpportunity + ',' + includeAccountNotes + ']';
    } else {
      includeTables = '"include" : [' + includeContact + ',' + includeSalesActivity + ']';
    }
    const whereCondition = '"where": {"id":' + accountId + ', "SID":' + this.authService.getSID() + ' }';

    const columnFilter = '"fields": ["name","account","id", "salesPerson", "property", "SID"]';
    const filter = '{' + whereCondition + ',' + columnFilter + ',' + includeTables + '}';
    const params = { params: this.ngaugeService.getHttpParams(filter) };
    return this.httpClient.get(this.ngaugeService.getMeldURL('ACCOUNT_OPTIMIZED'), params).map((accountDetails: any) => {
      const account = accountDetails[0];
      account.contact = this.ngaugeService.appendFNameAndLName(account.contact, account.SID);
      account.salesactivity = account.salesactivity.filter(activity => activity.SID === account.SID);
      account.salesactivity.forEach(activity => {
        if (activity['adminuser'] !== undefined) {
          activity['adminuser']['userName'] = `${activity['adminuser']['firstName']} ${activity['adminuser']['lastName']}`;
        } else {
          activity['adminuser'] = { userName: '' };
        }
      });
      return accountDetails;
    });
  }

  getAccountById(accountId) {
    const whereCondition = '"where": {"id": ' + accountId + ', "SID":"'+ this.authService.getSID() +'"}';
    const includeContact = '"include" : {"relation": "contact"}';
    const filter = '{' + whereCondition + ',' + includeContact + '}';
    const params = { params: this.ngaugeService.getHttpParams(filter) };
    return this.httpClient.get(this.ngaugeService.getMeldURL('ACCOUNT_OPTIMIZED'), params).map((accounts: any) => {
      accounts[0]['contact'] = this.ngaugeService.appendFNameAndLName(accounts[0]['contact'],accounts[0]['SID']);
      return accounts;
    });
  }

  getAccountBusinessOpp(accountId) {
    const whereCondition = '"where": {"id": ' + accountId + '}';
    const fields = '"fields": ["id", "account", "website", "companyType", "employeeCount", "officeType", "parentCompany"]';
    const filter = '{' + whereCondition + ',' + fields + '}';
    const params = { params: this.ngaugeService.getHttpParams(filter) };
    return this.httpClient.get(this.ngaugeService.getMeldURL('ACCOUNT_OPTIMIZED'), params);
  }

  getAccountProperty(accountId) {
    const whereCondition = '"where": {"id": ' + accountId + '}';
    const fields = '"fields":["property", "id"]';
    const filter = '{' + whereCondition + ',' + fields + '}';
    const params = { params: this.ngaugeService.getHttpParams(filter) };
    return this.httpClient.get(this.ngaugeService.getMeldURL('ACCOUNT_OPTIMIZED'), params);
  }

  getWhereCondition(accountId) {
    const userProperty = this.userService.getLoggedInUserProperty();
    return '"where": {"and":[{"accountId":' + accountId + '}, {"property": "' + userProperty + '"}]}';
  }

  getContactDetails(accountId) {
    const whereCondition = this.getWhereCondition(accountId);
    const columnFilter = '"fields": ["id", "image", "firstName", "lastName", "designation", "fax",' +
      '"phoneNumber", "type", "email", "workEmail", "assistant", "address", "city", "state", "zip", "assistantPhoneNo", "SID"]';
    const filter = '{' + whereCondition + ',' + columnFilter + '}';
    const SID = this.authService.getSID();
    const params = { params: this.ngaugeService.getHttpParams(filter) };
    return this.httpClient.get(this.ngaugeService.getMeldURL('CONTACT'), params).map((contacts: any) => {
      contacts = this.ngaugeService.appendFNameAndLName(contacts, SID);
      return contacts;
    });
  }

  getContactDetailSalActivity(accountId) {
    const whereCondition = this.getWhereCondition(accountId);
    const columnFilter = '"fields": ["id", "firstName", "lastName", "phoneNumber", "email", "workEmail","SID"]';
    const filter = '{' + whereCondition + ',' + columnFilter + '}';
    const params = { params: this.ngaugeService.getHttpParams(filter) };
    return this.httpClient.get(this.ngaugeService.getMeldURL('CONTACT'), params).map((contacts: any) => {
      contacts = this.ngaugeService.appendFNameAndLName(contacts, this.authService.getSID());
      return contacts;
    });
  }

  getContactDetailsByFNameLName(accountId) {
    const whereCondition = this.getWhereCondition(accountId);
    const columnFilter =
      '"fields": ["id", "firstName", "lastName", "phoneNumber", "email", "assistant", "fax", "workEmail", "address"]';
    const filter = '{' + whereCondition + ',' + columnFilter + '}';
    const params = { params: this.ngaugeService.getHttpParams(filter) };
    return this.httpClient.get(this.ngaugeService.getMeldURL('CONTACT'), params);
  }

  getUpdatedSalesActivity(accountId) {
    const whereCondition = '"where": {"accountId":' + accountId + '}';
    const columnFilter = '"fields": ["id", "createdBy","startDate", "endDate", "completed", "type",' +
      '"objective", "accountId", "salesActivityType","assigned", "completionDate", "result","SID"]';
    const includeAdminUser =
      '"include": {"relation": "adminuser", "scope":{"fields":["id","firstName","lastName"]}}';
    const filter = '{' + whereCondition + ',' + columnFilter + ',' + includeAdminUser + '}';
    const params = { params: this.ngaugeService.getHttpParams(filter) };
    return this.httpClient.get(this.ngaugeService.getMeldURL('SALES_ACTIVITY'), params).map((salesActivities: any) => {
      salesActivities.forEach(activity => {
        if (activity['adminuser'] !== undefined) {
          activity['adminuser']['userName'] = `${activity['adminuser']['firstName']} ${activity['adminuser']['lastName']}`;
        } else {
          activity['adminuser'] = { userName: '' };
        }
      });
      return salesActivities;
    });
  }

  getUpdatedBusinessOpp(accountId) {
    const whereCondition = this.getWhereCondition(accountId);
    const orderBy = '"order":"createdOn desc"';
    const includeAdminUser =
      '"include": {"relation": "adminuser", "scope":{"fields":["id","firstName","lastName"]}}';
    const filter = '{' + whereCondition + ',' + orderBy + ',' + includeAdminUser + '}';
    const params = { params: this.ngaugeService.getHttpParams(filter) };
    return this.httpClient.get(this.ngaugeService.getMeldURL('BUSINESS_OPPORTUNITIES'), params);
  }

  getUpdateCorpRates(accountId) {
    const whereCondition = this.getWhereCondition(accountId);
    const filter = '{' + whereCondition + '}';
    const params = { params: this.ngaugeService.getHttpParams(filter) };
    return this.httpClient.get(this.ngaugeService.getMeldURL('RATE'), params);
  }

  getUpdateTracking(accountId) {
    const whereCondition = this.getWhereCondition(accountId);
    const filter = '{' + whereCondition + '}';
    const params = { params: this.ngaugeService.getHttpParams(filter) };
    return this.httpClient.get(this.ngaugeService.getMeldURL('TRACK_ACCOUNT'), params);
  }

  getUpdateAccountNotes(accountId) {
    const whereCondition = this.getWhereCondition(accountId);
    const filter = '{' + whereCondition + '}';
    const params = { params: this.ngaugeService.getHttpParams(filter) };
    return this.httpClient.get(this.ngaugeService.getMeldURL('ACCOUNT_NOTES'), params);
  }


  getUpdateGroup(accountId) {
    const whereCondition = this.getWhereCondition(accountId);
    const columnFilter =
      '"fields" : ["id", "arrivalDate","endDate", "groupName", "contact", "status", "property", "roomBlockInfo",' +
      '"coordinator", "salesPerson"]';
    const filter = '{' + whereCondition + ',' + columnFilter + '}';
    const params = { params: this.ngaugeService.getHttpParams(filter) };
    return this.httpClient.get(this.ngaugeService.getMeldURL('GROUP_INFO'), params);
  }

  getUpdatedFunctions(accountId) {
    const whereCondition = this.getWhereCondition(accountId);
    const functionFields =
      '"fields": ["id", "functionDate", "functionName","contact", "contactId", "status", "cateringManager", "salesPerson", "groupname"]';
    const includeAdminUser =
      '"include": [{"relation": "adminuser", "scope":{"fields":["id","firstName","lastName"]}},' +
      ' {"relation": "contact", "scope":{"fields": ["id", "firstName", "lastName", ' +
      ' "phoneNumber", "type", "email", "workEmail"]}}]';
    const filter = '{' + whereCondition + ',' + functionFields + ',' + includeAdminUser + '}';
    const params = { params: this.ngaugeService.getHttpParams(filter) };
    return this.httpClient.get(this.ngaugeService.getMeldURL('GET_FUNCTION'), params);
  }

  checkForAccount(account) {
    const fields = '"fields": ["account","name"]';
    const whereCondition = '"where" : {"account" :"' + account + '"}';
    const filter = '{' + whereCondition + ',' + fields + '}';
    const params = { params: this.ngaugeService.getHttpParams(filter) };
    return this.httpClient.get(this.ngaugeService.getMeldURL('ACCOUNT_OPTIMIZED'), params);
  }

  getContactById(accountId) {
    const whereCondition = this.getWhereCondition(accountId);
    const columnFilter = '"fields": ["id", "firstName", "lastName", "title", "designation", "fax",' +
      '"phoneNumber", "email","assistant", "workEmail","SID"]';
    const filter = '{' + whereCondition + ',' + columnFilter + '}';
    const params = { params: this.ngaugeService.getHttpParams(filter) };
    return this.httpClient.get(this.ngaugeService.getMeldURL('CONTACT'), params);
  }

  addAccountDetails(accountDetails, contactDetails) {
    const params = {
      'accountdetail': accountDetails,
      'contactdetail': contactDetails,
      'SID': this.authService.getSID()
    };
    return this.httpClient.post(this.ngaugeService.getMeldURL('CREATE_ACCOUNT'), params);
  }

  updateAccountDetails(accountDetails) {
    const params = { 'accountdetail': accountDetails, 'SID': this.authService.getSID() };
    return this.httpClient.post(this.ngaugeService.getMeldURL('UPDATEACCOUNT'), params);
  }

  updateContactDetails(contactDetails) {
    const params = { 'contactdetail': contactDetails, 'SID': this.authService.getSID() };
    return this.httpClient.put(this.ngaugeService.getMeldURL('UPDATECONTACT'), params);
  }

  getAccountDetailsForDoc(accountId) {
    const contactFields = '"fields": ["id", "firstName", "lastName", "phoneNumber", "designation", "type", "city", "state",' +
      ' "zip", "address"]';
    const includeContact = '{"relation": "contact", "scope":{' + contactFields + '}}';

    const rateFields = '"fields": ["id", "type", "rate", "startDate", "endDate", "forecast","isActive"]';
    const includeRate = '{"relation": "rate", "scope": {' + rateFields + '}}';

    const salesActivityFields = '"fields": ["id", "createdBy", "startDate", "assigned", "result", "completionDate",' +
      ' "objective", "contact"]';
    const includeAdminUser = '"include": {"relation": "adminuser", "scope":{"fields":["id","firstName","lastName"]}}';
    const includeSalesActivity = '{"relation": "salesactivity", "scope":{' +
      salesActivityFields + ',' + includeAdminUser + '}}';

    const includeTable = '"include": [' + includeContact + ',' + includeRate + ',' + includeSalesActivity + ']';
    const columnFilter = '"fields": ["account", "address", "city", "state", "zipCode", "marketSegment", "business", "source",' +
      '"property", "id", "SID"]';
    const whereCondition = '"where": {"id":' + accountId +',"SID":'+this.authService.getSID()+ '}';
    const filter = '{' + whereCondition + ',' + columnFilter + ',' + includeTable + '}';
    const params = { params: this.ngaugeService.getHttpParams(filter) };
    return this.httpClient.get(this.ngaugeService.getMeldURL('ACCOUNT_OPTIMIZED'), params).map((accountDetails: any) => {
      accountDetails.forEach(account => {
        account.contact = this.ngaugeService.appendFNameAndLName(account.contact, account.SID);
      });
      return accountDetails;
    });
  }

  getNameAndIdFilter() {
    const columnFilter = '"fields": ["name", "id", "SID"],"where":{"SID":"' + this.authService.getSID() + '","name":{"neq":""}}';
    const orderby = '"order":"name asc"';
    return `{ ${columnFilter}, ${orderby} }`;
  }

  getSIDFilter() {
    const columnFilter = '"where":{"SID":"' + this.authService.getSID() + '"}';
    return `{ ${columnFilter} }`;
  }

  getAccountSourceList() {
    const filter = this.getNameAndIdFilter();
    const params = { params: this.ngaugeService.getHttpParams(filter) };
    return this.httpClient.get(this.ngaugeService.getMeldURL('GET_ACCOUNT_SOURCE_LIST'), params);
  }

  getAccountMarketList() {
    const filter = this.getNameAndIdFilter();
    const params = { params: this.ngaugeService.getHttpParams(filter) };
    return this.httpClient.get(this.ngaugeService.getMeldURL('GET_ACCOUNT_MARKET_LIST'), params);
  }

  getSalutations() {
    const filter = this.getSIDFilter();
    const params = { params: this.ngaugeService.getHttpParams(filter) };
    return this.httpClient.get(this.ngaugeService.getMeldURL('SALUTATION'), params);
  }

  getAccountPriorityList() {
    const filter = this.getNameAndIdFilter();
    const params = { params: this.ngaugeService.getHttpParams(filter) };
    return this.httpClient.get(this.ngaugeService.getMeldURL('GET_ACCOUNT_PRIORITY_LIST'), params);
  }

  getAccountBusinessList() {
    const filter = this.getNameAndIdFilter();
    const params = { params: this.ngaugeService.getHttpParams(filter) };
    return this.httpClient.get(this.ngaugeService.getMeldURL('GET_ACCOUNT_BUSINESS_LIST'), params);
  }

  getAccountTypesList() {
    const filter = this.getNameAndIdFilter();
    const params = { params: this.ngaugeService.getHttpParams(filter) };
    return this.httpClient.get(this.ngaugeService.getMeldURL('GET_ACCOUNT_TYPES_LIST'), params);
  }

  getOfficeTypesList() {
    const filter = this.getNameAndIdFilter();
    const params = { params: this.ngaugeService.getHttpParams(filter) };
    return this.httpClient.get(this.ngaugeService.getMeldURL('GET_ACCOUNT_OFFICE_TYPES_LIST'), params);
  }

  getRateTypesList() {
    const filter = this.getNameAndIdFilter();
    const params = { params: this.ngaugeService.getHttpParams(filter) };
    return this.httpClient.get(this.ngaugeService.getMeldURL('GET_ACCOUNT_RATE_SOURCE_LIST'), params);
  }

  getPropertyTypesList() {
    const userDetails = JSON.parse(sessionStorage.getItem(USER_INFO));
    const userType = userDetails['userType'];
    if (userType === SUPER_ADMIN) {
      const columnFilter = '"fields": ["name", "id" , "SID"]';
      const filter = '{' + columnFilter + '}';
      const params = { params: this.ngaugeService.getHttpParams(filter) };
      return this.httpClient.get(this.ngaugeService.getMeldURL('GET_UNIQUE_PROPERTY_LIST'), params);
    } else {
      const userProperty = [];
      userProperty.push({ name: userDetails['property'] });
      return of(userProperty);
    }
  }

  getTrackingTypesList() {
    const filter = this.getNameAndIdFilter();
    const params = { params: this.ngaugeService.getHttpParams(filter) };
    return this.httpClient.get(this.ngaugeService.getMeldURL('GET_ACCOUNT_TRACKING_TYPES_LIST'), params);
  }

  getTrackingLeadStatusList() {
    const filter = this.getNameAndIdFilter();
    const params = { params: this.ngaugeService.getHttpParams(filter) };
    return this.httpClient.get(this.ngaugeService.getMeldURL('GET_ACCOUNT_TRACKING_LEADSTATUS_LIST'), params);
  }

  getTags() {
    const filter = this.getNameAndIdFilter();
    const params = { params: this.ngaugeService.getHttpParams(filter) };
    return this.httpClient.get(this.ngaugeService.getMeldURL('GET_TAGS_LIST'), params);
  }

  getAccountPropertyDetails(propertyName, fields) {
    const whereCondition = '"where": {"name": "' + propertyName + '"}';
    const filter = '{' + whereCondition + ',' + fields + '}';
    const params = { params: this.ngaugeService.getHttpParams(filter) };
    return this.httpClient.get(this.ngaugeService.getMeldURL('GET_PROPERTY_LIST'), params);
  }

  getAccountWithoutSA(filters) {
    let params = new HttpParams();
    const values = {
      property: this.userService.getLoggedInUserProperty(),
      salesPerson: filters.touser,
      marketSegment: filters.toMarketSegment,
      SID:this.authService.getSID()
    };
    params = params.append('filterDetails', JSON.stringify(values));
    return this.httpClient.get(this.ngaugeService.getMeldURL('GET_ACCOUNT_SALES_ACTIVTY_INFO'), { params });
  }

  getAccountGroupArrivals(fromDate, toDate, salesPerson, property) {
    fromDate = DateUtil.getFormattedDate(fromDate);
    toDate = DateUtil.getFormattedDate(toDate);
    const betweenCondition = '{"between": ["' + fromDate + '", "' + toDate + '"]}';
    let conditions = '{ "arrivalDate": ' + betweenCondition + ' }, { "or": [{ "status": "Tentative" }, { "status": "Definite" }]}';
    if (salesPerson !== undefined) { conditions += ',{"salesPerson": ' + salesPerson + '}'; }
    if (property !== undefined) { conditions += ',{"property": "' + property + '"}'; }
    const whereCondition = '"where": {"and":[' + conditions + ']}';
    const includeAdminUser = '"include": {"relation": "adminuser", "scope":{"fields":["id","firstName","lastName"]}}';
    const fields = '"fields": ["arrivalDate","id","reservationInfo","endDate","groupName","status","groupType","groupId", "salesPerson"]';
    const filter = '{' + whereCondition + ',' + includeAdminUser + ',' + fields + '}';
    const params = { params: this.ngaugeService.getHttpParams(filter) };
    return this.httpClient.get(this.ngaugeService.getMeldURL('GROUP_INFO'), params);
  }

  addSalutation(values, userId) {
    const data = {
      createdBy: userId,
      createdOn: new Date(),
      name: values,
      id: 0
    };
    return this.httpClient.post(this.ngaugeService.getMeldURL('SALUTATION'), data);
  }

  removeSalutation(id) {
    return this.httpClient.delete(this.ngaugeService.getMeldURL('SALUTATION') + '/' + id);
  }

  deleteSalesActivity(id) {
    return this.httpClient.delete(this.ngaugeService.getMeldURL('SALES_ACTIVITY') + '/' + id);
  }

  checkForSalesActivity(contactId) {
    const whereCondition = '"where":{"contact":"' + contactId + '"}';
    const filter = '{' + whereCondition + '}';
    const params = { params: this.ngaugeService.getHttpParams(filter) };
    return this.httpClient.get(this.ngaugeService.getMeldURL('SALES_ACTIVITY'), params);
  }

  getTrackingLostReport(fromDate, toDate, property) {
    const betweenFilter = '"between":["' + fromDate + '", "' + toDate + '"]';
    const columnFilter = '"fields": ["accountId", "eventname", "date", "type", "night", "rate", "revenue", "notes", "trackingType",' +
      ' "roomRental", "banquetBar", "banquetFood", "createdOn", "id","SID"]';
    let whereCondition = '"where": {"date": {' + betweenFilter + '}}';
    if (property !== undefined) {
      whereCondition = '"where": {"and": [{"date": {' + betweenFilter + '}}, {"property": "' + property + '"}]}';
    }
    const includeFilter = '"include": {"relation":"account", "scope": {"fields":["id", "account"]}}';
    const filter = '{' + whereCondition + ',' + columnFilter + ',' + includeFilter + '}';
    const params = { params: this.ngaugeService.getHttpParams(filter) };
    return this.httpClient.get(this.ngaugeService.getMeldURL('TRACK_ACCOUNT'), params);
  }

  getGroupDetailsById(groupId) {
    const whereCondition = '"where": {"id": ' + groupId + '}';
    const includeSalesActivity = '"include": {"relation": "groupsalesactivity"}';
    const filter = '{' + whereCondition + ',' + includeSalesActivity + '}';
    const params = { params: this.ngaugeService.getHttpParams(filter) };
    return this.httpClient.get(this.ngaugeService.getMeldURL('GROUP_INFO'), params);
  }

  getFunctionDetailsById(functionId) {
    const whereCondition = '"where": {"id": ' + functionId + '}';
    const includeSalesActivity = '"include": {"relation": "functionsalesactivity"}';
    const filter = '{' + whereCondition + ',' + includeSalesActivity + '}';
    const params = { params: this.ngaugeService.getHttpParams(filter) };
    return this.httpClient.get(this.ngaugeService.getMeldURL('GET_FUNCTION'), params);
  }

  mergeAccount(oldId, id) {
    const accountinfo = { oldId, id };
    const SID = this.authService.getSID()
    return this.httpClient.post(this.ngaugeService.getMeldURL('MERGE_ACCOUNT'), { accountinfo, SID });
  }

  getAllFunctionsInfo(property) {
    let httpParams = new HttpParams();
    let sid=JSON.parse(sessionStorage.getItem('sid'))
    httpParams = httpParams.append('property', property);
    httpParams = httpParams.append('SID', sid);
    return this.httpClient.get(this.ngaugeService.getMeldURL('GETALLACCOUNTFUNCTIONS'), { params: httpParams });
  }

  getBeoTemplate() {
    const whereCondition = '"where": {"type": "BEO_Document"}';
    const fields = '"fields": ["title", "description", "id", "type"]';
    const filter = '{' + whereCondition + ',' + fields + '}';
    const params = { params: this.ngaugeService.getHttpParams(filter) };
    return this.httpClient.get(this.ngaugeService.getMeldURL('GET_CONTRACT_TEMPLATES'), params);
  }

  getaccountinfo(accountId) {
    const whereCondition = '"where": {"id": ' + accountId + ', "SID":"'+ this.authService.getSID() +'"}';
    const filter = '{' + whereCondition + '}';
    const params = { params: this.ngaugeService.getHttpParams(filter) };
    return this.httpClient.get(this.ngaugeService.getMeldURL('ACCOUNT_OPTIMIZED'), params).map((accounts: any) => {
      return accounts;
    });

  }
}



// WEBPACK FOOTER //
// ./src/app/services/accounts.service.ts